import { useMemo } from 'react';

import {
  Button,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Icons,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import { InlineCode } from 'components/InlineCode';
import {
  ALL_INSTANCES_BY_PROVIDER,
  ClmCompatibleFamiliesPicker,
  ClmCompatibleFamiliesPickerProvider,
  ClmInstanceSelection,
  useClmCompatibleFamiliesPicker,
} from 'features/cluster/autoscaler/components/ClmCompatibleFamiliesPicker';

const Footer = ({
  onSave,
  onCancel,
}: {
  onSave: (clmInstanceSelection: ClmInstanceSelection) => void;
  onCancel: () => void;
}) => {
  const { baseInstance, selectedInstances } = useClmCompatibleFamiliesPicker();

  return (
    <DrawerFooter>
      <Button
        variant="secondary"
        onClick={onCancel}
        testId="clm-cancel-selection-button"
      >
        Cancel
      </Button>
      <Button
        disabled={!baseInstance}
        testId="clm-save-selection-button"
        onClick={() => {
          if (!baseInstance) {
            return;
          }
          onSave({
            baseInstance,
            compatibleInstances: selectedInstances,
          });
        }}
      >
        Save selection
      </Button>
    </DrawerFooter>
  );
};

type Props = {
  initialClmInstanceSelection?: ClmInstanceSelection;
  onSave: (clmInstanceSelection: ClmInstanceSelection) => void;
  onCancel: () => void;
};

export const CompatibleInstancesSlide = ({
  initialClmInstanceSelection,
  onSave,
  onCancel,
}: Props) => {
  const availableInstances = useMemo(
    () => Array.from(ALL_INSTANCES_BY_PROVIDER.eks ?? []),
    []
  );

  return (
    <ClmCompatibleFamiliesPickerProvider
      availableInstances={availableInstances}
      initialSelection={initialClmInstanceSelection}
    >
      <DrawerHeader closeButtonProps={{ sx: { display: 'none' } }}>
        <Button
          size="sm"
          startIcon={<Icons.ArrowLeft />}
          variant="ghost"
          sx={{ ml: -16 }}
          onClick={onCancel}
          testId="back-to-setup-guide-button"
        >
          Back to setup guide
        </Button>
      </DrawerHeader>
      <DrawerBody>
        <DrawerContent>
          <ClmCompatibleFamiliesPicker
            header={
              <Stack gap={4} maxWidth={560}>
                <Typography
                  variant="L12R"
                  color={tokens.theme.fg.structural.secondary}
                  pb={4}
                >
                  Container live migration
                </Typography>

                <Typography variant="h6">
                  Find compatible instance families
                </Typography>

                <Typography variant="P12R" color="text.secondary">
                  Select a base family to see which other families share the
                  same CPU generation — migrations only succeed between
                  compatible types.
                </Typography>
              </Stack>
            }
            summaryHeader={
              <Stack gap={4} maxWidth={560}>
                <Typography variant="P14M">
                  Compatible instance selector
                </Typography>

                <Typography variant="P12R" color="text.secondary">
                  Select a base family to see compatible options. Once selected,
                  you&apos;ll need to apply these manually to your
                  NodePool&apos;s&nbsp;
                  <InlineCode>spec.requirements</InlineCode>
                </Typography>
              </Stack>
            }
          />
        </DrawerContent>
      </DrawerBody>
      <Footer onSave={onSave} onCancel={onCancel} />
    </ClmCompatibleFamiliesPickerProvider>
  );
};
